// Tryb — studio home.

import { ENRICHMENT } from "@shared/fields.js";
import { empty } from "@shared/validation.js";
import { Badge, Button, Icon } from "../../ds/index.js";
import { Card, EmptyState, InkCard, Meter, Overline, SectionTitle } from "../../components/Card.jsx";
import { FieldCell } from "../../components/TrybField.jsx";
import { useStudio } from "../../lib/studioContext.js";
import { useFieldViews, iconForCategory } from "../../lib/useFieldViews.js";
import { certificationProgress, seasonMetrics, trybRow } from "../../lib/studioView.js";

export default function Home({ onNavigate, onStartListing, onOpenCover }) {
  const { vals, state, trybs, verifyStatus } = useStudio();
  const { fieldView, fieldViews } = useFieldViews({ onOpenCover });

  const isNew = state.stage === "Brand-new";
  const isDone = state.stage === "Season 1 complete";
  const hostFirst = String(vals["A1.02"] || "there").split(" ")[0];

  const cert = certificationProgress(vals);
  const metrics = seasonMetrics(state.stage, cert.done);
  const hasTryb = trybs.length > 0;
  const live = trybs[0];

  const checklist = [
    { label: "Account set up", state: "Done", icon: "Check", bg: "var(--sage-100)", fg: "var(--sage-700)" },
    {
      label: "Identity verified",
      state: verifyStatus === "Verified" ? "Done" : verifyStatus === "Pending" ? "Being checked" : "Needed to publish",
      icon: verifyStatus === "Verified" ? "Check" : "Clock",
      bg: verifyStatus === "Verified" ? "var(--sage-100)" : "var(--honey-100)",
      fg: verifyStatus === "Verified" ? "var(--sage-700)" : "var(--honey-600)",
    },
    {
      label: "First tryb published",
      state: hasTryb ? "Done" : "About 15 minutes",
      icon: hasTryb ? "Check" : "Calendar",
      bg: hasTryb ? "var(--sage-100)" : "var(--sand-200)",
      fg: hasTryb ? "var(--sage-700)" : "var(--text-secondary)",
    },
    { label: "Certification badge", state: `${cert.done} of 31`, icon: "Star", bg: "var(--sand-200)", fg: "var(--text-secondary)" },
  ];

  const enrichDone = ENRICHMENT.filter((e) => !empty(vals[e.id])).length;
  const showReup = isDone && !state.reupPublished;

  return (
    <>
      {isNew ? (
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20, alignItems: "stretch" }}>
          <InkCard
            padding={32}
            style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 24, position: "relative", overflow: "hidden" }}
          >
            <div style={{ position: "absolute", right: -20, bottom: -30, opacity: 0.14, color: "var(--sand-75)" }}>
              <Icon name="Users" size={220} sw={1.2} />
            </div>
            <div style={{ position: "relative" }}>
              <Overline color="inherit" style={{ opacity: 0.65 }}>
                You’re set up
              </Overline>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-2xl)",
                  fontWeight: 800,
                  letterSpacing: "var(--tracking-tight)",
                  lineHeight: 1.1,
                  marginTop: 8,
                }}
              >
                Hi {hostFirst}. Ready to put something on the calendar?
              </div>
              <div style={{ fontSize: "var(--text-sm)", opacity: 0.8, lineHeight: 1.55, marginTop: 10, maxWidth: 440 }}>
                The room is the product. Most facilitators start with eight seats and a coffee afterwards, so nobody in it goes unnoticed. We’ve filled in the sensible defaults — you change
                what’s different about yours.
              </div>
            </div>
            <div style={{ position: "relative", display: "flex", gap: 10 }}>
              <Button variant="primary" size="lg" onClick={onStartListing}>
                Start a tryb
              </Button>
              <Button variant="ghost" size="lg" onClick={() => onNavigate("venues")} style={{ color: "var(--sand-75)" }}>
                Add a venue first
              </Button>
            </div>
          </InkCard>

          <Card style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <SectionTitle>Where you are</SectionTitle>
            {checklist.map((c) => (
              <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span
                  style={{
                    width: 28, height: 28, borderRadius: "50%", flex: "none",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    background: c.bg, color: c.fg,
                  }}
                >
                  <Icon name={c.icon} size={15} />
                </span>
                <span style={{ flex: 1, fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)" }}>{c.label}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{c.state}</span>
              </div>
            ))}
          </Card>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20 }}>
          <Card style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <div
              style={{
                width: 72, height: 72, borderRadius: "var(--radius-md)", background: "var(--sage-600)", color: "var(--sand-50)",
                display: "flex", alignItems: "center", justifyContent: "center", flex: "none",
              }}
            >
              <Icon name="Users" size={34} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Overline>{isDone ? "Season 1 · wrapped" : "Season 1 · week 2 of 6"}</Overline>
              <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-xl)", fontWeight: 800, letterSpacing: "var(--tracking-tight)", marginTop: 4 }}>
                {live?.title}
              </div>
              <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", marginTop: 4 }}>
                {live ? `${(live.day || "").slice(0, 3).toUpperCase()} · ${live.time} · ${live.hood} · ${live.sub || live.cat}` : ""}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-3xl)", fontWeight: 800, color: "var(--ember-700)", lineHeight: 1 }}>
                {live ? `${live.joined} of ${live.total}` : ""}
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                {live ? `JOINED · ${live.wait} WAITING` : ""}
              </div>
            </div>
          </Card>

          <Card>
            <Overline>Who’s in the room</Overline>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
              {[
                { k: "Ages", v: "27–35" },
                { k: "Mix", v: "4 women · 3 men · 1 nb" },
                { k: "Came alone", v: "5 of 8" },
              ].map((c) => (
                <div key={c.k} style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--text-sm)" }}>
                  <span style={{ color: "var(--text-secondary)" }}>{c.k}</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-primary)" }}>{c.v}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", marginTop: 12, lineHeight: 1.5 }}>
              Just the shape of the group — never names, never faces.
            </div>
          </Card>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        {metrics.map((m) => (
          <Card key={m.key} padding={20}>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", fontWeight: 600 }}>{m.key}</div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "var(--text-3xl)",
                fontWeight: 800,
                lineHeight: 1.1,
                margin: "6px 0",
                letterSpacing: "var(--tracking-tight)",
                color: m.color,
              }}
            >
              {m.value}
            </div>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", lineHeight: 1.45 }}>{m.sub}</div>
          </Card>
        ))}
      </div>

      {showReup && (
        <div
          style={{
            background: "var(--honey-100)",
            border: "1px solid var(--honey-300)",
            borderRadius: "var(--radius-xl)",
            padding: "24px 28px",
            display: "flex",
            alignItems: "center",
            gap: 20,
          }}
        >
          <div style={{ flex: 1 }}>
            <SectionTitle>Season one’s done. Seven of eight want to come back.</SectionTitle>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.5 }}>
              Everything’s carried over. Pick the dates and you’re done. Three people are already waiting for a seat.
            </div>
          </div>
          <Button variant="primary" onClick={() => onNavigate("reup")}>
            Confirm season 2
          </Button>
        </div>
      )}

      {!isNew && (
        <Card padding={28}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
            <div>
              <Overline color="var(--ember-700)">While it’s fresh</Overline>
              <h2 style={{ margin: "6px 0 4px", fontFamily: "var(--font-display)", fontSize: "var(--text-lg)", fontWeight: 800, letterSpacing: "var(--tracking-snug)" }}>
                You ran your first night. Six quick things while it’s fresh.
              </h2>
              <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                None of these are required. Each one makes your tryb sound more like you.
              </div>
            </div>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
              {enrichDone} of 6
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {ENRICHMENT.map((e) => {
              const f = fieldView(e.id, false);
              if (!f) return null;
              return (
                <div
                  key={e.id}
                  style={{
                    border: "1px solid var(--border-subtle)",
                    borderRadius: "var(--radius-lg)",
                    padding: 18,
                    background: "var(--surface-raised)",
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-base)", fontWeight: 700, lineHeight: 1.3 }}>{e.q}</div>
                    {!empty(vals[e.id]) && <Badge tone="success">Saved</Badge>}
                  </div>
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", lineHeight: 1.5 }}>{e.why}</div>
                  <div style={{ minWidth: 0 }}>
                    <FieldCell f={{ ...f, cols: 1 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20, alignItems: "start" }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <SectionTitle>Your trybs</SectionTitle>
            <Button size="sm" variant="ghost" onClick={() => onNavigate("trybs")}>
              See all
            </Button>
          </div>

          {!hasTryb && (
            <EmptyState>Nothing here yet. The first one takes about fifteen minutes. After that, a new season is two clicks.</EmptyState>
          )}

          <div style={{ display: "flex", flexDirection: "column" }}>
            {trybs.map((t) => {
              const g = trybRow(t, state.reupPublished, iconForCategory);
              return (
                <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderTop: "1px solid var(--border-subtle)" }}>
                  <div
                    style={{
                      width: 44, height: 44, borderRadius: "var(--radius-sm)", background: g.color, color: "var(--sand-50)",
                      display: "flex", alignItems: "center", justifyContent: "center", flex: "none",
                    }}
                  >
                    <Icon name={g.icon} size={22} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.title}</div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{g.meta}</div>
                  </div>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>{g.seats}</span>
                  <Badge tone={g.tone} dot>
                    {g.status}
                  </Badge>
                </div>
              );
            })}
          </div>
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <SectionTitle>Certification</SectionTitle>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{cert.done} of 31</span>
            </div>
            <Meter pct={cert.pct} color="var(--honey-500)" style={{ margin: "14px 0 10px" }} />
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", lineHeight: 1.5 }}>
              Optional to publish. Needed for the badge. The badge is how you get found.
            </div>
            <div style={{ marginTop: 14 }}>
              <Button size="sm" variant="secondary" onClick={() => onNavigate("cert")}>
                Continue
              </Button>
            </div>
          </Card>

          <InkCard>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)", fontWeight: 800, marginBottom: 6, letterSpacing: "var(--tracking-snug)" }}>
              We don’t rank on fill rate.
            </div>
            <div style={{ fontSize: "var(--text-sm)", opacity: 0.85, lineHeight: 1.55 }}>
              What moves you up is people coming back, because that’s what belonging looks like from here. A packed room nobody returns to ranks below a half-full one that does.
            </div>
          </InkCard>
        </div>
      </div>
    </>
  );
}
